import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

/**
 * Hook to handle payment success callbacks
 * Invalidates queries and shows success message
 */
export function usePaymentSuccess() {
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    const paymentSuccess =
      searchParams?.get("payment-success") || searchParams?.get("success");
    const refresh = searchParams?.get("refresh");
    
    // Handle specific payment errors passed from callback
    const error = searchParams?.get("error");
    const reason = searchParams?.get("reason");
    
    if (error) {
      console.error("❌ Payment error detected:", { error, reason });
      const errorMessage = reason 
        ? `Payment Failed: ${decodeURIComponent(reason)}`
        : "Payment processing failed. Please try again.";
      
      toast.error(errorMessage, { duration: 6000 });
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, "/classes");
      return;
    }

    if (paymentSuccess === "true" || refresh) {
      console.log("✅ Payment success detected in URL, refreshing data...", {
        paymentSuccess,
        refresh,
      });

      // Force refresh with longer delay for payment success
      const delay = paymentSuccess === "true" ? 2000 : 500;

      const timeout = setTimeout(async () => {
        console.log("🔄 Force refreshing all data after payment success");

        // Invalidate queries to refetch
        await queryClient.invalidateQueries({ queryKey: ["userBookings"] });
        await queryClient.invalidateQueries({ queryKey: ["classes"] });

        if (paymentSuccess === "true") {
          toast.success("Payment successful! Your booking has been confirmed.");
        }

        // Clear URL parameters after refresh
        window.history.replaceState({}, document.title, "/classes");
      }, delay);

      return () => clearTimeout(timeout);
    }
  }, [searchParams, queryClient]);
}
